import {useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import axios from 'axios'
import {KTSVG} from '../../../../_metronic/helpers'
import {parseResultsSearch} from '../resultsParams'
import {buildResultsUrl} from '../routing'
import {globalSearch, createCancelSource} from '../../../services/searchService'
import {getExperts, PublicExpert} from '../../../services/expertsService'
import apiClient from '../../../helpers/apiClient'
import {MemberSearchList} from '../../members/components/MemberSearchList'
import {ExpertCardList} from '../experts/components/ExpertCardList'
import {ExperienceCard} from '../../members/components/ExperienceCard'
import {CommunityDiscussionsItem} from '../../community/components/CommunitySectionItem'
import {CommunityDiscussion} from '../../community/models/CommunityDiscuss'
import {ExperienceSummary} from '../../teamExperiences/models/ExperienceSummaryModel'
import {SearchType} from '../types'
import {meetsMinLength} from '../utils'
import {labelForType} from '../../../../_metronic/layout/components/header/search/searchTypeMeta'
import {useUserProfileContext} from '../../../context/UserProfileContext'

// Section order requested for /search.
const SECTION_ORDER: SearchType[] = ['members', 'experiences', 'expertise', 'forums']

const PREVIEW_LIMIT = 8
// Members and Expert use the native card layout from /members-search (flex-wrap with 290–305px
// cards) — one full row typically renders 5 cards, so cap the preview to 5 to avoid trailing a
// half-filled row here.
const CARD_LIMIT = 5

const subheadingStyle: React.CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: 22,
  fontWeight: 500,
  color: '#151515',
  margin: '0 0 18px',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'left',
}

const sectionWrap: React.CSSProperties = {marginTop: 40}

const asList = (data: any): any[] => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

// Hydrate specific ids returned by the aggregator using the `?ids=` filter added to each view.
// Payload stays proportional to the bucket size (≤5 rows/type) instead of loading whole listings.
const fetchMembersByIds = async (ids: string[]): Promise<any[]> => {
  if (ids.length === 0) return []
  const {data} = await apiClient.get('/users/search/', {
    params: {ids: ids.join(','), limit: ids.length},
  })
  return asList(data)
}

const fetchExperiencesByIds = async (ids: string[]): Promise<any[]> => {
  if (ids.length === 0) return []
  const {data} = await apiClient.get('/experiences/search/', {
    params: {ids: ids.join(','), limit: ids.length},
  })
  return asList(data)
}

const fetchForumsByIds = async (ids: string[]): Promise<any[]> => {
  if (ids.length === 0) return []
  const {data} = await apiClient.get('/community/discussions/', {
    params: {post_type: 'forum', status: 'published', ids: ids.join(','), limit: ids.length},
  })
  return asList(data)
}

const adaptDiscussion = (raw: any): CommunityDiscussion => ({
  id: raw?.id,
  host: !!raw?.host,
  notifications: raw?.notifications ?? 0,
  avatar: raw?.avatar ?? '',
  author: raw?.author ?? '',
  title: raw?.title ?? '',
  tags: Array.isArray(raw?.tags) ? raw.tags : [],
  details: raw?.details ?? '',
  comments: raw?.comments ?? 0,
  members: raw?.members ?? 0,
  memberComments: raw?.memberComments ?? raw?.member_comments ?? 0,
})

const SearchAllPage = () => {
  const location = useLocation()
  const history = useHistory()
  const {setSearchParams: setUserProfile} = useUserProfileContext()

  const [members, setMembers] = useState<any[]>([])
  const [experiences, setExperiences] = useState<ExperienceSummary[]>([])
  const [experts, setExperts] = useState<PublicExpert[]>([])
  const [forums, setForums] = useState<CommunityDiscussion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const {q} = parseResultsSearch(location.search)
  const trimmed = (q ?? '').trim()
  const hasQuery = trimmed.length > 0
  const canFetch = meetsMinLength(trimmed)

  useEffect(() => {
    const container = document.getElementById('kt_content_container')
    container?.classList.add('member-full-wrapper')
    return () => container?.classList.remove('member-full-wrapper')
  }, [])

  useEffect(() => {
    if (!canFetch) {
      setMembers([])
      setExperiences([])
      setExperts([])
      setForums([])
      setError(null)
      return
    }

    const source = createCancelSource()
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        // Aggregator is the source of truth for WHICH items appear in each section — same items
        // the header dropdown shows when scope='All'. Fire the aggregate + experts in parallel;
        // once we know the ids per bucket, hydrate each type with a targeted `?ids=` fetch (≤5
        // rows/type) instead of loading whole listings. Experts are already rich from
        // ExpertSearchView so they don't need hydration.
        const [aggregate, expertsRaw] = await Promise.all([
          globalSearch(trimmed, 'all', undefined, undefined, {cancelToken: source.token}),
          getExperts(trimmed, ''),
        ])

        if (cancelled) return

        const memberBucketIds = (aggregate.buckets.members?.items ?? []).map((i) =>
          String((i as any).id)
        )
        const experienceBucketIds = (aggregate.buckets.experiences?.items ?? []).map((i) =>
          String((i as any).id)
        )
        const forumBucketIds = (aggregate.buckets.forums?.items ?? []).map((i) =>
          String((i as any).id)
        )

        const [membersRaw, experiencesRaw, forumsRaw] = await Promise.all([
          fetchMembersByIds(memberBucketIds),
          fetchExperiencesByIds(experienceBucketIds),
          fetchForumsByIds(forumBucketIds),
        ])

        if (cancelled) return

        const membersById = new Map(membersRaw.map((m) => [String(m.id), m]))
        const hydratedMembers = memberBucketIds
          .map((id) => membersById.get(id))
          .filter(Boolean)

        const experiencesById = new Map(experiencesRaw.map((e) => [String(e.id), e]))
        const hydratedExperiences = experienceBucketIds
          .map((id) => experiencesById.get(id))
          .filter(Boolean) as ExperienceSummary[]

        // Experts: the aggregator's expertise bucket returns ExpertiseService rows (services),
        // whereas /expert-search and the dropdown scope='Expert' surface Partners. Use the
        // same Partner source here so the section stays consistent with the dedicated page.
        const hydratedExperts = asList(expertsRaw) as PublicExpert[]

        const forumsById = new Map(forumsRaw.map((f) => [String(f.id), f]))
        const hydratedForums = forumBucketIds
          .map((id) => forumsById.get(id))
          .filter(Boolean)
          .map(adaptDiscussion)

        setMembers(hydratedMembers)
        setExperiences(hydratedExperiences)
        setExperts(hydratedExperts)
        setForums(hydratedForums)
      } catch (err) {
        if (axios.isCancel(err)) return
        if (cancelled) return
        setError(err as Error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
      source.cancel('search superseded')
    }
  }, [trimmed, canFetch])

  const goToDetailMember = (id: string) => {
    setUserProfile({userSelected: id})
    history.push('/member/overview')
  }
  const goToDetailExperience = (id: number | string) => history.push(`/experience/detail/${id}`)
  const goToDetailExpert = (id: number | string) => history.push(`/experts/${id}`)
  const goToDetailForum = (id: number) => history.push(`/community/details/${id}`)

  const goToSection = (searchType: SearchType) => {
    history.push(buildResultsUrl(trimmed, searchType))
  }

  const sectionTotals: Record<SearchType, number> = {
    all: 0,
    members: members.length,
    experiences: experiences.length,
    expertise: experts.length,
    forums: forums.length,
    mastermind_circles: 0,
  }

  const renderSectionHeader = (searchType: SearchType) => (
    <button
      type='button'
      style={subheadingStyle}
      onClick={() => goToSection(searchType)}
      aria-label={`See all ${labelForType(searchType).toLowerCase()} results`}
    >
      {labelForType(searchType)} ({sectionTotals[searchType]})
    </button>
  )

  const renderSection = (searchType: SearchType) => {
    switch (searchType) {
      case 'members': {
        if (members.length === 0) return null
        return (
          <div key='members' style={sectionWrap}>
            {renderSectionHeader('members')}
            <MemberSearchList
              data={members.slice(0, CARD_LIMIT)}
              loading={false}
              onItemClick={goToDetailMember}
            />
          </div>
        )
      }
      case 'experiences': {
        if (experiences.length === 0) return null
        return (
          <div key='experiences' style={sectionWrap}>
            {renderSectionHeader('experiences')}
            <div className='container-experience-search-wrapper-cards'>
              {experiences.slice(0, PREVIEW_LIMIT).map((item, idx) => (
                <div
                  key={`exp-${item.id ?? idx}`}
                  onClick={() => goToDetailExperience(item.id)}
                  style={{cursor: 'pointer'}}
                >
                  <ExperienceCard experience={item} />
                </div>
              ))}
            </div>
          </div>
        )
      }
      case 'expertise': {
        if (experts.length === 0) return null
        return (
          <div key='expertise' style={sectionWrap}>
            {renderSectionHeader('expertise')}
            <ExpertCardList
              data={experts.slice(0, CARD_LIMIT)}
              loading={false}
              onItemClick={(id) => goToDetailExpert(id)}
            />
          </div>
        )
      }
      case 'forums': {
        if (forums.length === 0) return null
        return (
          <div key='forums' style={sectionWrap}>
            {renderSectionHeader('forums')}
            <div className='community-list-items'>
              {forums.slice(0, PREVIEW_LIMIT).map((f) => (
                <CommunityDiscussionsItem
                  key={`forum-${f.id}`}
                  id={f.id}
                  host={f.host}
                  notifications={f.notifications}
                  avatar={f.avatar}
                  author={f.author}
                  title={f.title}
                  tags={f.tags}
                  details={f.details}
                  comments={f.comments}
                  members={f.members}
                  memberComments={f.memberComments}
                  onEnter={goToDetailForum}
                  onEdit={() => undefined}
                  onArchive={() => undefined}
                />
              ))}
            </div>
          </div>
        )
      }
      default:
        return null
    }
  }

  const totalCount = members.length + experiences.length + experts.length + forums.length

  return (
    <div className='members-page members-search-page search-all-page'>
      <div className='members-page__header' style={{marginTop: 32}}>
        <h1 className='members-page__title'>
          Results&nbsp;
          <KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
        </h1>
      </div>

      {!hasQuery && (
        <div style={{padding: '32px 0', color: '#666', fontSize: 16, textAlign: 'center'}}>
          Type a keyword in the header search to find results across every section.
        </div>
      )}

      {hasQuery && !canFetch && (
        <div style={{padding: '32px 0', color: '#666', fontSize: 16, textAlign: 'center'}}>
          Type at least 2 characters.
        </div>
      )}

      {canFetch && loading && totalCount === 0 && (
        <div style={{padding: '32px 0', color: '#666', fontSize: 16, textAlign: 'center'}}>
          Searching…
        </div>
      )}

      {canFetch && !loading && !error && totalCount === 0 && (
        <div style={{padding: '32px 0', color: '#666', fontSize: 16, textAlign: 'center'}}>
          No results for &ldquo;{trimmed}&rdquo;.
        </div>
      )}

      {SECTION_ORDER.map((t) => renderSection(t))}
    </div>
  )
}

export {SearchAllPage}
